/** Hjälpfunktioner för redaktionellt innehåll som importerats från elmarknad.se. */
import { LEGACY_MEDIA_BASE, LEGACY_UPLOADS_PATTERN } from "@/lib/articleImage";
import { rewriteMarkdownLinks } from "@/lib/internalLinks";


/** Tar bort SEO-suffix som "| Elmarknad.se" ur en rubrik. */
export function cleanArticleTitle(title?: string | null): string {
  if (!title) return "";
  return title.replace(/\s*[|\-–»]\s*Elmarknad(\.se)?\s*$/i, "").trim();
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Rensar bort dubbletter i artikeltexten: kontaktmejl, byline-rader,
 * en inledande H1 (som redan visas som sidrubrik) och hero-bilden.
 */
export function cleanArticleMarkdown(
  markdown?: string | null,
  heroImage?: string | null,
): string {
  if (!markdown) return "";
  let md = markdown;

  // Ta bort hero-bilden om den även ligger i brödtexten.
  if (heroImage) {
    const file = heroImage.split("/").pop()?.split("?")[0];
    if (file) {
      md = md.replace(
        new RegExp(`!\\[[^\\]]*\\]\\([^)]*${escapeRegExp(file)}[^)]*\\)\\s*`, "gi"),
        "",
      );
    }
  }

  // Mediabiblioteket är kopierat till vår egen lagring – peka om dit.
  md = md.replace(LEGACY_UPLOADS_PATTERN, LEGACY_MEDIA_BASE);

  // Bilder under gamla /Images finns inte kvar någonstans.
  md = md.replace(/!\[[^\]]*\]\([^)]*elmarknad\.se\/Images\/[^)]*\)\s*/gi, "");

  // Inskrapade "bilder" som egentligen pekar på en sida (ingen filändelse)
  // renderas som en trasig dubblett av toppbilden – plocka bort dem.
  md = md.replace(/!\[[^\]]*\]\(([^)]+)\)\s*/gi, (match, url: string) =>
    /\.(jpe?g|png|webp|gif|avif|svg)(\?|#|$)/i.test(String(url)) ? match : "",
  );



  // Ta bort det inscrapade nyhetsbrevsformuläret (vi har en egen komponent).
  md = md.replace(
    /\n#{1,4}\s*Prenumerera på nyhetsbrev[\s\S]*?(?=\n#{1,4}\s|$)/gi,
    "\n",
  );

  // Klipp bort mallsektioner från gamla sajten (widget, omdömen, generell FAQ, CTA).
  const BOILERPLATE_HEADINGS = [
    "Jämför elavtal",
    "Jämför elpriser",
    "Så säger våra kunder",
    "Vanliga frågor & svar",
    "Vanliga frågor och svar om Elmarknad",
    "Sänk din energikostnad",
    "Om Elmarknad.se",
  ];
  const boilerplateStart = md.search(
    new RegExp(
      `\\n#{2,4}\\s*(?:${BOILERPLATE_HEADINGS.map(escapeRegExp).join("|")})\\s*\\n`,
      "i",
    ),
  );
  if (boilerplateStart > 0) md = md.slice(0, boilerplateStart);
  // Rest av betygsblock ("### 4.9/5" + "+10.000 nöjda kunder")
  md = md.replace(/\n#{2,4}\s*\d[.,]\d\/5[\s\S]*$/i, "\n");

  const lines = md.split("\n");
  const cleaned: string[] = [];
  let removedH1 = false;

  for (const line of lines) {
    const trimmed = line.trim();
    // Rester från nyhetsbrevsformuläret
    if (/^Aktivera JavaScript i din webbläsare/i.test(trimmed)) continue;
    if (/^Skicka!\[Laddar\]/i.test(trimmed)) continue;
    // Kontaktmejl-rad högst upp
    if (/^\[?info@elmarknad\.se\]?/i.test(trimmed)) continue;
    // Byline: "av [Namn](...) | jan 14, 2026 | [Blogg](...)"
    if (/^av\s+\[/i.test(trimmed)) continue;
    // Mall-länkar från gamla sajtens sidhuvud ("Aktuella elpriser", "Jämför elavtal")
    if (
      /^\[(Aktuella elpriser|Jämför elavtal|Jämför elpriser)\]\([^)]*\)$/i.test(trimmed)
    )
      continue;
    // Första H1 dubblerar sidrubriken
    if (!removedH1 && /^#\s+/.test(trimmed)) {
      removedH1 = true;
      continue;
    }
    // Sidan har redan sin H1 – fler H1:or i texten blir H2 (gav dubbla h1 på bolagssidor).
    if (/^#\s+/.test(trimmed)) {
      cleaned.push(trimmed.replace(/^#\s+/, "## "));
      continue;
    }
    // Fetstil på egen rad var rubriker på gamla sajten – gör om till riktiga H2.
    const pseudoHeading = trimmed.match(/^\*\*([^*]{2,90}?)\*\*$/)?.[1]?.trim();
    if (pseudoHeading && !/[.:!?]$/.test(pseudoHeading)) {
      cleaned.push(`## ${pseudoHeading}`);
      continue;
    }
    cleaned.push(line);
  }

  // Interna länkar till gamla/omdirigerade URL:er pekas direkt på slutdestinationen.
  return rewriteMarkdownLinks(cleaned.join("\n").replace(/^\s+/, "").replace(/\n{3,}/g, "\n\n"));
}


/** Formaterar ett publiceringsdatum på svenska, t.ex. "14 januari 2026". */
export function formatArticleDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Gör rubriknivåerna i en artikel sammanhängande under sidans h1: den högsta
 * nivån blir h2 och ingen nivå hoppas över (t.ex. ## följt av #### blir ## och
 * ###). Innehåll från gamla sajten blandar nivåerna fritt, vilket ger fel
 * rubrikordning för skärmläsare och i tillgänglighetstester.
 */
export function normalizeHeadingLevels(markdown: string): string {
  const lines = markdown.split("\n");
  let inFence = false;
  const levels = new Set<number>();
  for (const line of lines) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    const m = !inFence && line.match(/^(#{1,6})\s+\S/);
    if (m) levels.add(m[1]!.length);
  }
  if (levels.size === 0) return markdown;
  const sorted = [...levels].sort((a, b) => a - b);
  const map = new Map(sorted.map((lvl, i) => [lvl, Math.min(6, i + 2)]));
  inFence = false;
  return lines
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
      if (inFence) return line;
      return line.replace(/^(#{1,6})(\s+)/, (_, hashes: string, space: string) => "#".repeat(map.get(hashes.length) ?? hashes.length) + space);
    })
    .join("\n");
}
